import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Save, CreditCard, ShieldCheck, AlertTriangle, ShoppingBag } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';

const inputCls = 'mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/35';
const sectionCls = 'rounded-3xl border-white/10 bg-white/[0.03] p-6 backdrop-blur';

export default function SettingsTab() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  // Use the ADMIN settings endpoint so we receive PayPal secrets in the editor
  useEffect(() => { api.get('/settings/admin').then(({ data }) => setS(data)); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/settings', s);
      setS(data);
      refresh();
      toast.success('Settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  if (!s) return <div className="text-white/55">Loading settings…</div>;

  return (
    <div className="space-y-6">
      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">Home page hero</div>
        <div className="text-xs text-white/55 mb-5">Background YouTube video, headline and sub-headline.</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Hero YouTube URL</Label>
            <Input data-testid="admin-settings-hero-video-input" className={inputCls} placeholder="https://www.youtube.com/watch?v=..." value={s.hero_video_url || ''} onChange={(e) => set('hero_video_url', e.target.value)} />
            <div className="text-[11px] text-white/45 mt-1">Tip: paste any YouTube link — we auto-convert it to an embed. Use the special params for autoplay/loop/no-controls.</div>
          </div>
          <div><Label>Hero headline</Label><Input className={inputCls} value={s.hero_headline || ''} onChange={(e) => set('hero_headline', e.target.value)} /></div>
          <div><Label>Hero poster image URL (fallback)</Label><Input className={inputCls} value={s.hero_poster_url || ''} onChange={(e) => set('hero_poster_url', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Hero subheadline</Label><Textarea rows={3} className={inputCls} value={s.hero_subheadline || ''} onChange={(e) => set('hero_subheadline', e.target.value)} /></div>
        </div>
      </Card>

      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">Site-wide statistics</div>
        <div className="text-xs text-white/55 mb-5">Numbers shown on Home and About pages.</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><Label>Years experience</Label><Input className={inputCls} value={s.stat_years || ''} onChange={(e) => set('stat_years', e.target.value)} /></div>
          <div><Label>Divers trained</Label><Input className={inputCls} value={s.stat_divers || ''} onChange={(e) => set('stat_divers', e.target.value)} /></div>
          <div><Label>Courses offered</Label><Input className={inputCls} value={s.stat_courses || ''} onChange={(e) => set('stat_courses', e.target.value)} /></div>
          <div><Label>Rating</Label><Input className={inputCls} value={s.stat_rating || ''} onChange={(e) => set('stat_rating', e.target.value)} /></div>
        </div>
      </Card>

      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">About page</div>
        <Label>About YouTube URL</Label>
        <Input className={inputCls} placeholder="https://www.youtube.com/watch?v=..." value={s.about_video_url || ''} onChange={(e) => set('about_video_url', e.target.value)} />
      </Card>

      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">Course-page backgrounds</div>
        <div className="text-xs text-white/55 mb-5">Image shown behind the SDI / TDI flowchart pages (with dark overlay).</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>SDI background URL</Label><Input className={inputCls} value={s.sdi_background_url || ''} onChange={(e) => set('sdi_background_url', e.target.value)} /></div>
          <div><Label>TDI background URL</Label><Input className={inputCls} value={s.tdi_background_url || ''} onChange={(e) => set('tdi_background_url', e.target.value)} /></div>
        </div>
      </Card>

      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">Social & review links</div>
        <div className="text-xs text-white/55 mb-5">Leave any blank to hide its icon on the site.</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Instagram URL</Label><Input data-testid="admin-settings-instagram-input" className={inputCls} placeholder="https://instagram.com/..." value={s.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} /></div>
          <div><Label>Facebook URL</Label><Input data-testid="admin-settings-facebook-input" className={inputCls} placeholder="https://facebook.com/..." value={s.facebook_url || ''} onChange={(e) => set('facebook_url', e.target.value)} /></div>
          <div><Label>WhatsApp URL</Label><Input data-testid="admin-settings-whatsapp-input" className={inputCls} placeholder="https://wa.me/9715..." value={s.whatsapp_url || ''} onChange={(e) => set('whatsapp_url', e.target.value)} /></div>
          <div><Label>TripAdvisor URL</Label><Input className={inputCls} placeholder="https://www.tripadvisor.com/..." value={s.tripadvisor_url || ''} onChange={(e) => set('tripadvisor_url', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Google Review URL</Label><Input className={inputCls} placeholder="https://g.page/r/..." value={s.google_review_url || ''} onChange={(e) => set('google_review_url', e.target.value)} /></div>
        </div>
      </Card>

      <Card className={sectionCls}>
        <div className="font-display text-lg font-semibold mb-1">Contact</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Contact email</Label><Input className={inputCls} value={s.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} /></div>
          <div><Label>Contact phone</Label><Input className={inputCls} value={s.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} /></div>
          <div className="sm:col-span-2">
            <Label>Complaints / contact form destination email</Label>
            <Input
              data-testid="admin-complaints-email"
              className={inputCls}
              type="email"
              placeholder="complaints@arabdivertours.com"
              value={s.complaints_email || ''}
              onChange={(e) => set('complaints_email', e.target.value)}
            />
            <div className="text-[11px] text-white/45 mt-1">
              Where contact-form / complaint messages from <code className="text-[#00b4d8]">/contact</code> are delivered. Replies you send go directly to the customer&apos;s email. Leave empty to fall back to the booking inbox.
            </div>
          </div>
          <div className="sm:col-span-2"><Label>Footer copyright text</Label><Input className={inputCls} value={s.footer_copyright || ''} onChange={(e) => set('footer_copyright', e.target.value)} /></div>
        </div>
      </Card>

      <Card className={sectionCls} data-testid="admin-shop-settings">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingBag className="h-5 w-5 text-[#00b4d8]" />
          <div className="font-display text-lg font-semibold">Shop &amp; delivery</div>
        </div>
        <div className="text-xs text-white/55 mb-5">
          Shipping fee added to every shop order at checkout. Change it any time — customers see the new fee instantly.
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>UAE delivery fee (AED)</Label>
            <Input
              data-testid="admin-shop-shipping-fee"
              className={inputCls}
              type="number"
              min={0}
              step="1"
              placeholder="50"
              value={s.shop_shipping_fee_aed ?? 50}
              onChange={(e) => set('shop_shipping_fee_aed', e.target.value === '' ? '' : Number(e.target.value))}
            />
            <div className="text-[11px] text-white/45 mt-1">
              Set to <code className="text-[#00b4d8]">0</code> for free delivery. Applied on top of the item subtotal.
            </div>
          </div>
        </div>
      </Card>

      <Card className={sectionCls}>
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="h-5 w-5 text-[#0078d4]" />
          <div className="font-display text-lg font-semibold">PayPal payments</div>
          <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.paypal_enabled ? 'bg-[#c8e600]/15 text-[#c8e600] border border-[#c8e600]/40' : 'bg-white/5 text-white/55 border border-white/15'}`}>
            {s.paypal_enabled ? 'Live on site' : 'Disabled'}
          </span>
        </div>
        <div className="text-xs text-white/55 mb-5">
          Enter your credentials from{' '}
          <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-[#00b4d8] underline">PayPal Developer Dashboard</a>.
          The <span className="text-white/80">Client&nbsp;Secret</span> and <span className="text-white/80">Webhook&nbsp;ID</span> never leave the server &mdash; the website only sees the Client&nbsp;ID matching the currently selected mode.
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div>
              <Label className="text-white">Enable PayPal on the booking form</Label>
              <div className="text-[11px] text-white/55 mt-0.5">When off, customers see the placeholder &ldquo;Pay Now (PayPal coming soon)&rdquo;.</div>
            </div>
            <Switch
              data-testid="admin-paypal-enabled"
              checked={!!s.paypal_enabled}
              onCheckedChange={(v) => set('paypal_enabled', v)}
            />
          </div>

          <div>
            <Label>Mode</Label>
            <Select value={s.paypal_mode || 'sandbox'} onValueChange={(v) => set('paypal_mode', v)}>
              <SelectTrigger data-testid="admin-paypal-mode" className={`${inputCls} w-full`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#07101f] border-white/15 text-white">
                <SelectItem value="sandbox" className="focus:bg-white/10 focus:text-white">Sandbox (testing)</SelectItem>
                <SelectItem value="live" className="focus:bg-white/10 focus:text-white">Live (real money)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Charge currency</Label>
            <Select value={s.paypal_currency || 'USD'} onValueChange={(v) => set('paypal_currency', v)}>
              <SelectTrigger data-testid="admin-paypal-currency" className={`${inputCls} w-full`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#07101f] border-white/15 text-white">
                <SelectItem value="USD" className="focus:bg-white/10 focus:text-white">USD &mdash; US Dollar (recommended for AED peg)</SelectItem>
                <SelectItem value="EUR" className="focus:bg-white/10 focus:text-white">EUR &mdash; Euro</SelectItem>
                <SelectItem value="GBP" className="focus:bg-white/10 focus:text-white">GBP &mdash; British Pound</SelectItem>
                <SelectItem value="CHF" className="focus:bg-white/10 focus:text-white">CHF &mdash; Swiss Franc</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-white/45 mt-1">PayPal Checkout does not support AED directly. Prices on the site stay in AED; PayPal charges in this currency using the FX rate below.</div>
          </div>

          <div>
            <Label>Deposit % charged at booking</Label>
            <Input
              data-testid="admin-paypal-deposit"
              className={inputCls}
              type="number"
              min={1}
              max={100}
              value={s.paypal_deposit_pct ?? 100}
              onChange={(e) => set('paypal_deposit_pct', e.target.value === '' ? '' : Number(e.target.value))}
            />
            <div className="text-[11px] text-white/45 mt-1">100 = full payment up-front. 30 = customer pays 30% deposit online, remainder offline.</div>
          </div>
          <div>
            <Label>AED &rarr; USD FX rate</Label>
            <Input
              data-testid="admin-paypal-fx"
              className={inputCls}
              type="number"
              step="0.0001"
              value={s.paypal_aed_fx_rate ?? 3.6725}
              onChange={(e) => set('paypal_aed_fx_rate', e.target.value === '' ? '' : Number(e.target.value))}
            />
            <div className="text-[11px] text-white/45 mt-1">AED is pegged to USD at 3.6725. Only change this if the peg moves.</div>
          </div>

          <div className="sm:col-span-2">
            <Label>Webhook ID (created in PayPal Dashboard &rarr; Webhooks)</Label>
            <Input
              data-testid="admin-paypal-webhook-id"
              className={inputCls}
              placeholder="8X12345ABC..."
              value={s.paypal_webhook_id || ''}
              onChange={(e) => set('paypal_webhook_id', e.target.value)}
            />
            <div className="text-[11px] text-white/45 mt-1">
              Webhook URL to register in PayPal:{' '}
              <code className="text-[#00b4d8]">{`${typeof window !== 'undefined' ? window.location.origin : ''}/api/payments/paypal/webhook`}</code>
            </div>
          </div>

          <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#00b4d8] font-semibold mb-3">Sandbox credentials (testing)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Sandbox Client ID</Label>
                <Input data-testid="admin-paypal-sandbox-client" className={inputCls} value={s.paypal_sandbox_client_id || ''} onChange={(e) => set('paypal_sandbox_client_id', e.target.value)} placeholder="AaSandbox..." />
              </div>
              <div>
                <Label>Sandbox Secret</Label>
                <Input data-testid="admin-paypal-sandbox-secret" className={inputCls} type="password" value={s.paypal_sandbox_secret || ''} onChange={(e) => set('paypal_sandbox_secret', e.target.value)} placeholder="EHSandbox..." />
              </div>
            </div>
          </div>

          <div className="sm:col-span-2 rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.05] p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-[#c8e600]" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#c8e600] font-semibold">Live credentials (production)</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Live Client ID</Label>
                <Input data-testid="admin-paypal-live-client" className={inputCls} value={s.paypal_live_client_id || ''} onChange={(e) => set('paypal_live_client_id', e.target.value)} placeholder="AaLive..." />
              </div>
              <div>
                <Label>Live Secret</Label>
                <Input data-testid="admin-paypal-live-secret" className={inputCls} type="password" value={s.paypal_live_secret || ''} onChange={(e) => set('paypal_live_secret', e.target.value)} placeholder="EHLive..." />
              </div>
            </div>
            {s.paypal_mode === 'live' && s.paypal_enabled && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-amber-100 text-xs">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>Live mode is active. Real money will be charged on every successful booking.</div>
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label>Notification email (where to send &ldquo;PAID&rdquo; emails)</Label>
            <Input
              data-testid="admin-paypal-notify-email"
              className={inputCls}
              value={s.paypal_notification_email || ''}
              onChange={(e) => set('paypal_notification_email', e.target.value)}
              placeholder="leave empty to use the default booking inbox"
            />
          </div>
        </div>
      </Card>

      <div className="sticky bottom-4">
        <div className="flex justify-end">
          <Button data-testid="admin-save-button" onClick={save} disabled={saving} className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl h-11 px-6">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save settings</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
